import { defineStore } from 'pinia'
import { computed, onScopeDispose, ref } from 'vue'

import { i18n } from '@/i18n'
import { parseCallableError } from '@/lib/firebase/errors'
import { createRetainer } from '@/lib/live/retainer'
import {
  type WritePostInput,
  subscribePostRun as repoSubscribePostRun,
  subscribeLatestPostRun as repoSubscribeLatestPostRun,
  writePost as repoWritePost,
} from '@/repositories/post-run'
import { useKeyedSubscription } from '@/stores/keyed-subscription'
import { currentSetupScope, useLatestRun } from '@/stores/setup-subscriptions'
import { useToastStore } from '@/stores/toast'
import { isLoaded } from '@/types/load-state'
import { POST_RUN_STALE_AFTER_MS, type PostRun } from '@/types/post-run'

/** The brand's post run: its latest run and the start of a new one. */
export const usePostRunStore = defineStore('post-run', () => {
  const latest = useLatestRun<PostRun>(repoSubscribeLatestPostRun, POST_RUN_STALE_AFTER_MS)
  const byId = useKeyedSubscription<PostRun>(repoSubscribePostRun)
  const starting = ref(false)

  const run = latest.run
  const isLive = latest.isLive
  const settled = computed(() => latest.state.value.status !== 'loading')

  const retainer = createRetainer({
    open() {
      const scope = currentSetupScope()
      if (scope) latest.open(scope)
      else latest.fail(i18n.global.t('common.noWorkspaceSelected'))
    },
    close() {
      latest.close()
    },
  })
  onScopeDispose(retainer.dispose)

  /** The run a holder retains through `retainRun`, or `null` while it loads or when it is absent. */
  function runById(runId: string | null): PostRun | null {
    const state = byId.state.value
    return runId !== null && byId.key.value === runId && isLoaded(state) ? state.value : null
  }

  /** Opens a post run; resolves the run id, or `false` on failure (toasted). */
  async function write(input: WritePostInput = {}): Promise<string | false> {
    starting.value = true
    try {
      return (await repoWritePost(input)).runId
    } catch (cause) {
      useToastStore().error(
        i18n.global.t('content.toast.writePostFailed'),
        parseCallableError(cause).message,
      )
      return false
    } finally {
      starting.value = false
    }
  }

  return {
    run,
    isLive,
    settled,
    starting,
    retain: retainer.retain,
    retainRun: byId.retain,
    runById,
    write,
  }
})
